import { useState } from "react";
import ExpenseForm from "../entry/ExpenseForm";
import Modal from "../../components/Modal";
import { api } from "../../lib/api";
import { formatMoney } from "../../lib/money-math";
import type { DashboardData, ExpenseInput, Owner, Recurring } from "../../types";

/** A bill's rule, translated into the same starting point the expense form always takes — pre-filled,
 * never auto-saved. The person reviews and can change anything (a different account, a category that
 * changed since the bill was set up, this cycle's actual amount) before confirming. */
function billToExpenseInput(bill: Recurring): ExpenseInput {
  return {
    requestId: crypto.randomUUID(),
    type: bill.type === "Income" || bill.type === "Transfer" ? bill.type : "Expense",
    accountId: bill.accountId,
    ...(bill.type === "Transfer" && bill.destinationAccountId ? { toAccountId: bill.destinationAccountId } : {}),
    amount: bill.amount ? formatMoney(bill.amount) : "",
    date: bill.nextDue.slice(0, 10),
    category: bill.category || "Uncategorized",
    subcategory: bill.subcategory || "",
    title: bill.name,
    notes: "",
    ...(bill.counterpartyId ? { payeeId: bill.counterpartyId } : {}),
  };
}

export default function PayBill({
  data,
  profile,
  bill,
  onClose,
  onPaid,
}: {
  data: DashboardData;
  profile: Owner;
  bill: Recurring;
  onClose: () => void;
  onPaid: () => Promise<unknown>;
}) {
  const [error, setError] = useState("");
  return (
    <Modal title={`${bill.type === "Income" ? "Receive" : "Pay"} ${bill.name}`} onClose={onClose}>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <ExpenseForm allowConversion={false}
        data={data}
        profile={profile}
        initialInput={billToExpenseInput(bill)}
        reviewLabel={bill.type === "Income" ? "Mark received" : "Pay bill"}
        onSaved={() => {}}
        onReview={async (expense) => {
          try {
            setError("");
            await api("bills/pay", { ...expense, scheduledItemId: bill.id });
            await onPaid();
            onClose();
          } catch (e) {
            setError((e as Error).message);
          }
        }}
      />
    </Modal>
  );
}
